/**
 * The tracking **writes** — the calls the Integrations screen's controls make.
 * The tracking read is not here; see below.
 *
 *   saveTracking          PUT    /tracking                        the three switches
 *   requestRedirectDomain PUT    /tracking/redirect-domain        start the CNAME flow
 *   checkRedirectDomain   POST   /tracking/redirect-domain/check  "check DNS now"
 *   clearRedirectDomain   DELETE /tracking/redirect-domain        abandon the flow
 *   provisionTracking     POST   /tracking/configuration-set      retry provisioning
 *   setUpDeliveryReports  POST   /tracking/delivery-reports       the delivery reports set alone
 *   setTrackingTopic      PUT    /tracking/sns-topic              nominate the SNS topic
 *   fetchWebhookUrl       GET    /webhooks/ses/url                the URL to subscribe it to
 *
 * **`fetchTracking` is imported from `lib/api/tracking.ts`, not written again
 * here.** That module already owns `GET /tracking`, because the template editor
 * reads the same settings to decide which of slice 4's two opposite sentences
 * to print beside a preview. Two implementations of one GET is two places for a
 * response shape to be parsed differently, and the way that goes wrong is that
 * one of them keeps rendering the strong privacy promise from a field the other
 * has already learned to distrust. This module re-exports it so a component on
 * this screen still has one import for the endpoint set.
 *
 * **Why the writes are here and not there.** They belong in `lib/api/tracking.ts`
 * beside the read, and they are not there yet only because slice 11 is being
 * built by several agents at once and that file is another one's: appending to
 * it in parallel is a merge conflict in a module neither of us was really
 * editing. Nothing about the code depends on the location — fold these writes
 * into `lib/api/tracking.ts` the moment the slice is one person's again. The
 * same note is already written in `lib/api/integrations.ts` about the two
 * response envelopes it declares, for the same reason and with the same expiry.
 *
 * **Every route answers the whole of `TrackingResponse`, and that is a choice.**
 * Verifying a CNAME can flip `redirectDomain` from `pending` to `verified`,
 * which is a precondition for click tracking; provisioning changes what
 * `configurationSetNameForSend` would return; a settings write can be refused
 * for a reason that lives in a *different* field than the one that was toggled.
 * A response carrying only the fragment that was written would leave the screen
 * merging server state into local state and getting it wrong exactly when the
 * two disagree — which is the only time it matters. One shape, whole, every
 * time: the screen replaces rather than merges, and cannot drift.
 *
 * **The request bodies are the shared contracts, parsed before they leave.**
 * `trackingSettingsUpdateSchema` and `trackingRedirectDomainRequestSchema` are
 * the same objects the API validates against, so a body this app could not
 * construct is a body the API never has to refuse. That matters most for VDM:
 * the enabled arm requires `costAcknowledged: z.literal(true)`, so a browser
 * that has not shown somebody the price literally cannot express the request
 * that would put a surcharge on their AWS bill. The parse here is where that
 * becomes true of *this* app rather than only of the server.
 *
 * **Nothing here reaches AWS from the browser.** Every one of these is a call to
 * Wend's API, which holds the tenant's sealed SES credential and talks to AWS
 * on its own side. There is no path from this file to an AWS endpoint, and the
 * responses carry no credential — `trackingResponseSchema` is `.strict()` and
 * has no field one could arrive in, so an API that started returning one would
 * fail parsing here as well as there.
 *
 * ROUTE PATHS ARE STILL LOCAL KNOWLEDGE, but they are no longer a guess. When
 * this file was first written the API side of slice 11 did not exist and these
 * six paths were assumptions. `apps/api/src/modules/integrations/tracking.controller.ts`
 * has since landed on `@Controller('tracking')` with exactly these routes, and
 * each literal below has been checked against it. `@wend/shared` still carries
 * the shapes and not the paths, so this remains the one place in the web app
 * that names them and the whole of the change if they ever move.
 *
 * **The one route on that controller this file does not call is `PUT /tracking`'s
 * sibling `GET`** — that is `lib/api/tracking.ts`, re-exported below.
 */
import {
  trackingResponseSchema,
  trackingRedirectDomainRequestSchema,
  trackingSettingsUpdateSchema,
  type TrackingRedirectDomainRequest,
  type TrackingResponse,
  type TrackingSettingsUpdate,
} from '@wend/shared';
import { z } from 'zod';

import { apiRequest } from '../../lib/api/client';

/**
 * The read, from the module that owns it. Re-exported rather than re-declared —
 * see the header. A tenant who has never touched tracking gets `TRACKING_OFF`
 * from the API rather than a 404, so there is no absence for this screen to
 * translate and no second place for the default to be invented.
 */
export { fetchTracking } from '../../lib/api/tracking';

/** The base every route below hangs off. Named once so it moves once. */
const TRACKING_PATH = '/tracking';
const REDIRECT_DOMAIN_PATH = `${TRACKING_PATH}/redirect-domain`;

/* -------------------------------------------------------------------------- */
/* Write                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Writes all three switches at once. `PUT`, because it replaces wholesale.
 *
 * The body is `{ open, click, vdm }` — never one field — and that is the
 * contract's shape rather than this module's convenience.
 * `evaluateTrackingChange` refuses a request *whole* when any part of it is
 * refused, on the argument that "we did half of what you asked and did not say
 * which half" is worse than a refusal. A partial body would make that
 * impossible to honour: the server would have to guess what the unsent fields
 * were meant to be, and its guess would be whatever it last stored, which is
 * exactly the state a concurrent change has already moved.
 *
 * So every caller on the screen builds the whole update from the settings it is
 * currently showing and changes one field of it. Two people editing the same
 * tenant's tracking in two tabs will therefore have a last-write-wins race over
 * three booleans — which is the right trade here, and the alternative
 * (per-field PATCH) buys nothing, because the refusals are cross-field anyway.
 *
 * A refusal is **not** a 200 with a decision in it. `evaluateTrackingChange`
 * has already been run on this screen before the button was enabled, so a
 * refusal arriving here means the server saw something the browser could not —
 * a domain that stopped verifying, an SES account disconnected in another tab.
 * That is an error condition for this screen and arrives as an `ApiError`
 * carrying the refusal's own message, which is safe to show and safe to log.
 */
export async function saveTracking(update: TrackingSettingsUpdate): Promise<TrackingResponse> {
  return apiRequest({
    path: TRACKING_PATH,
    method: 'PUT',
    body: trackingSettingsUpdateSchema.parse(update),
    schema: trackingResponseSchema,
  });
}

/* -------------------------------------------------------------------------- */
/* The redirect domain                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Claims a redirect subdomain and starts the CNAME flow.
 *
 * `PUT` rather than `POST` because it is idempotent: the same subdomain
 * submitted twice leaves the same pending record, which matters because the
 * obvious thing to do when a verification is taking a while is to submit it
 * again. Submitting a *different* one replaces the first, and the screen says
 * so before it happens — the previous CNAME becomes dead weight in the tenant's
 * DNS rather than something Wend can clean up.
 *
 * The response's `redirectDomain` comes back `pending` with the exact record to
 * create. The screen displays what it was given and computes nothing: the value
 * depends on the AWS region, AWS has moved its tracking hosts before, and a
 * frontend that derived it from a hard-coded string would be a frontend handing
 * somebody a DNS record that silently does nothing.
 */
export async function requestRedirectDomain(
  request: TrackingRedirectDomainRequest,
): Promise<TrackingResponse> {
  return apiRequest({
    path: REDIRECT_DOMAIN_PATH,
    method: 'PUT',
    body: trackingRedirectDomainRequestSchema.parse(request),
    schema: trackingResponseSchema,
  });
}

/**
 * Asks the API to look at DNS again. The "Check now" button.
 *
 * No body — the domain is on the server and re-sending it would let a browser
 * check a domain other than the one it claimed. This reaches out of Wend's
 * process to resolve a record and to ask AWS about a certificate, so the API
 * rate-limits it; a 429 arrives as an `ApiError` carrying `retryAfterSeconds`,
 * which the screen shows rather than inviting somebody to keep pressing.
 *
 * A check that comes back still-unverified is a **200**, not a failure. The
 * record not having propagated yet is the ordinary state of this flow for its
 * first several minutes, and rendering it as an error would teach people to
 * ignore the errors on this screen. The failure `reason` on the pending arm is
 * what says which of the five situations it is.
 */
export async function checkRedirectDomain(): Promise<TrackingResponse> {
  return apiRequest({
    path: `${REDIRECT_DOMAIN_PATH}/check`,
    method: 'POST',
    schema: trackingResponseSchema,
  });
}

/**
 * Abandons the redirect domain and returns to `not_configured`.
 *
 * This switches click tracking off as a consequence, because
 * `trackingSettingsSchema`'s click-on arm requires a verified domain *by type*
 * — there is no state where the domain is gone and click tracking survives, and
 * the API cannot construct one. The screen says that out loud before the button
 * is pressed rather than letting a person discover it afterwards.
 *
 * It does not touch the CNAME in the tenant's DNS, which Wend has no access to.
 * The record is left resolving to an AWS host that no longer routes for this
 * account, and the screen says that too — an orphaned CNAME is the sort of
 * thing that is discovered years later by somebody auditing a zone file.
 */
export async function clearRedirectDomain(): Promise<TrackingResponse> {
  return apiRequest({
    path: REDIRECT_DOMAIN_PATH,
    method: 'DELETE',
    schema: trackingResponseSchema,
  });
}

/* -------------------------------------------------------------------------- */
/* Provisioning                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Creates — or repairs — the configuration set, its event destination and its
 * tracking options in the tenant's own AWS account.
 *
 * This is the button behind the `needs_configuration_set` refusal, and it is
 * offered rather than hidden because that refusal is **Wend's fault, not the
 * tenant's**: it means provisioning failed or was never attempted, and the only
 * repair is to run it again. A refusal a person cannot act on is a dead end
 * with a paragraph attached.
 *
 * Safe to press twice. The configuration set's name is derived from the tenant
 * id rather than generated — `configurationSetNameForTenant` — precisely so
 * that a run which failed halfway is finished by the next one instead of
 * leaving a second, orphaned set in somebody's AWS account for ever.
 *
 * `POST` rather than `PUT`: it is not a description of a desired state the
 * caller supplies, it is "go and do the thing", and it has no body to be
 * idempotent *about*. Idempotence here is a property of the derived name, which
 * is where it belongs.
 */
export async function provisionTracking(): Promise<TrackingResponse> {
  return apiRequest({
    path: `${TRACKING_PATH}/configuration-set`,
    method: 'POST',
    schema: trackingResponseSchema,
  });
}

/**
 * Creates — or repairs — the delivery reports set alone, and changes no switch.
 *
 * Not `provisionTracking`, which also reconciles the tracking set to the
 * switches. After a re-connect with a new access key the switches are kept and
 * both sets are forgotten, so a workspace whose open switch still says on is
 * sending untracked — and a button about delivery reports that reconciled the
 * tracking set too would put the pixel back. That is "Set up tracking in my AWS
 * account"'s decision, not this one's.
 */
export async function setUpDeliveryReports(): Promise<TrackingResponse> {
  return apiRequest({
    path: `${TRACKING_PATH}/delivery-reports`,
    method: 'POST',
    schema: trackingResponseSchema,
  });
}

/* -------------------------------------------------------------------------- */
/* The SNS topic                                                               */
/* -------------------------------------------------------------------------- */

/**
 * The shape of an SNS topic ARN, restated in the browser.
 *
 * **This is a duplicated contract and it should not stay one.** The authority
 * is `snsTopicArnSchema` in `apps/api/src/modules/integrations/tracking-view.ts`,
 * which is itself a restatement of the `integrations_tracking_topic_arn_shape`
 * CHECK constraint in migration 0014. That makes three copies of one regex, and
 * this is the third.
 *
 * It is here rather than imported because `apps/web` cannot import from
 * `apps/api` — they are separate applications that share only `@wend/shared` —
 * and `@wend/shared` has no SNS vocabulary at all: slice 11's shared contracts
 * were written before the API chose to have the tenant nominate a topic rather
 * than have Wend create one, so there was nothing to put an ARN schema beside.
 * The right home is `@wend/shared`, and `packages/shared` is owned by another
 * agent this slice, so adding it there now would collide. **This slice's report
 * carries it: move `snsTopicArnSchema` into `@wend/shared` and have all three
 * of these import it.**
 *
 * The duplication is safe in exactly one respect and unsafe in another, and
 * both are worth being explicit about. It is safe because the API validates
 * independently and the database refuses independently, so a browser copy that
 * drifts *looser* cannot store a malformed ARN — it can only produce a 400
 * where it should have produced a field error. It is unsafe because a copy that
 * drifts *tighter* rejects an ARN the API would have accepted, and the person
 * hits a validation message on a value that was correct. That is the direction
 * to watch when AWS adds a partition or lengthens a topic name.
 */
const SNS_TOPIC_ARN = /^arn:aws[a-z0-9-]*:sns:[a-z0-9-]+:[0-9]{12}:[A-Za-z0-9_-]{1,256}$/;

export const snsTopicArnSchema = z
  .string()
  .trim()
  .regex(
    SNS_TOPIC_ARN,
    // The API's own message, verbatim, including the example. A person who
    // triggers this in the browser and then again on the server must not be
    // told two different things about one value.
    'That does not look like an SNS topic ARN. It looks like arn:aws:sns:ap-south-1:123456789012:my-topic.',
  );

export const trackingTopicRequestSchema = z.object({ snsTopicArn: snsTopicArnSchema }).strict();
export type TrackingTopicRequest = z.infer<typeof trackingTopicRequestSchema>;

/**
 * Nominates the SNS topic the configuration set's event destination publishes
 * to — the missing input behind the provisioning failure this screen shows.
 *
 * **Why the tenant supplies this rather than Wend creating it.** Creating a
 * topic would mean asking for `sns:CreateTopic` and `sns:Subscribe` across a
 * customer's AWS account, which is a materially larger permission than sending
 * mail, for a step most tenants performed in slice 9 when they wired up bounce
 * and complaint notifications. Wend fills in the part that cannot be done by
 * hand without silently going wrong — the configuration set — and leaves the
 * part that can.
 *
 * `PUT`, and idempotent: the same ARN twice leaves the same nomination. A
 * *different* ARN repoints a live event destination, which is why the API rate
 * limits this route and why the panel says what repointing does before it
 * happens — events already in flight to the old topic do not come back.
 *
 * The API refuses a topic whose region does not match the SES credentials',
 * because Amazon will not publish SES events across regions. That arrives as an
 * `ApiError` naming both regions, and is shown against the field rather than as
 * a banner, because it is a fact about the value that was typed.
 */
export async function setTrackingTopic(request: TrackingTopicRequest): Promise<TrackingResponse> {
  return apiRequest({
    path: `${TRACKING_PATH}/sns-topic`,
    method: 'PUT',
    body: trackingTopicRequestSchema.parse(request),
    schema: trackingResponseSchema,
  });
}

/* -------------------------------------------------------------------------- */
/* The webhook URL                                                             */
/* -------------------------------------------------------------------------- */

/**
 * This workspace's own webhook URL — what the SNS topic has to be subscribed to
 * for any of this to arrive.
 *
 * The response shape is declared here for the same reason the ARN schema above
 * is: `webhookUrlResponseSchema` lives in `apps/api/src/modules/webhooks/webhook-view.ts`
 * and is deliberately API-local, on the argument that it is a composition for
 * one screen rather than shared vocabulary. That screen is this one, so this is
 * the other half of that composition. It is one `url` field and both halves are
 * `.strict()`, so the failure mode of drift is a parse error here rather than
 * a silently missing value.
 *
 * **Minting is deterministic and carries no side effect.** `WebhookTokenService`
 * derives the URL from the tenant rather than storing it, so reading it twice
 * returns the same string and reading it does not rotate anything. That is what
 * makes it safe to fetch on render, which is what this panel does.
 *
 * The URL contains a bearer-ish token in its path and is therefore a secret in
 * the weak sense: anyone holding it can post delivery events for this tenant.
 * The webhook ingest verifies the SNS signature before it believes anything, so
 * the token alone does not let somebody forge a bounce — but it is still not a
 * thing to paste into a support ticket, and the panel says so.
 */
export const webhookUrlResponseSchema = z.object({ url: z.string().url() }).strict();
export type WebhookUrlResponse = z.infer<typeof webhookUrlResponseSchema>;

export async function fetchWebhookUrl(signal?: AbortSignal): Promise<WebhookUrlResponse> {
  return apiRequest({
    path: '/webhooks/ses/url',
    schema: webhookUrlResponseSchema,
    ...(signal === undefined ? {} : { signal }),
  });
}
